import * as d3 from 'd3';
import Graph from './Graph';
import * as ncApi from '../apiRequests';
import { removeEl, removeNoDeleteAnimation } from './graphUtils';
import {
	createClusters,
	appendClusterCircle,
	clusterKeyFn,
	updateCluster,
} from './graphClusterUtils';
import { removeChildren, showAlert, createDialog, createButtons } from '../ui/uiUtils';
import { ClusterProps } from '../../types';
import { refreshHeartedList } from '../ui/ideaBox';
import { startIdeateTutorial } from '../tutorials/ideateTutorial';
import * as tutorialEvents from '../tutorials/tutorialEvents';
import * as templateTutorial from '../tutorials/templateTutorial';
import {
	AvailableColour,
	NCCluster,
	NCGraphData,
	BoardUser,
	GraphMode,
	UserSelection,
	ClusterAnalysis,
	NCNode,
	PositioningGenerationContext,
	PositioningLayout,
	PositioningSourceContext,
	GroundingEvidence,
	IdeateGroundingSnapshot,
	UserFile,
} from '../../../../src/commonTypes';
import { ANALYSES_TYPES, AnalysisKey } from '../../../../src/commonConstants';
import {
	analysesForPositioningGenerate,
	ensurePositioningClusterCenter,
	hasPositioningSourceContext,
	missingIdeateSourceMessage,
	resolveIdeateContextCopy,
	resolveIdeatePositioning,
} from '../../../../src/services/board/ideatePositioning';
import {
	buildGroundingContextClusters,
	buildSocialLandscapeSubclusters,
	buildSocialLandscapeSegments,
	findGroundingEvidenceByNodeLabel,
	GROUNDING_CONTEXT_ANALYSIS_KEY,
	groundingEvidenceNodeLabel,
	groundingContextNodeKey,
	hasGroundingContextBoardData,
	layoutGroundingContextClusters,
	selectedGroundingContextLabels,
	selectedGroundingContextNodeKeys,
	socialLandscapeSegmentEvidence,
} from '../../../../src/services/board/ideateGroundingBoard';
import { isLongNodeLabel } from '../../../../src/graphNodeShape';
import { resolvePdfContextFiles } from '../../../../src/services/pdf/pdfContextSelection';
import * as hints from '../tutorials/hints';
import { isMiniTutorialHidden } from '../tutorials/miniTutorial';
import {
	IdeateWorkspaceController,
	PositioningLane,
	setupIdeateWorkspace,
} from '../ui/ideateWorkspace';

const CLUSTER_SPACING = 350;
const IDEATE_WELCOME_RELEASE = 'five-branch-positioning-v1';
const POSITIONING_MAP_MIN_ZOOM = 0.25;
const POSITIONING_MAP_OVERVIEW_ZOOM = 0.62;
const POSITIONING_MAP_FAR_OVERVIEW_ZOOM = 0.38;

const groundingEvidenceKey = (item: GroundingEvidence): string =>
	`${item.source}::${item.title}::${item.url || ''}::${item.detail}`;

const groundingSourceLabel = (source: string): string => {
	if (source.startsWith('social-profile:')) {
		const platform = source.slice('social-profile:'.length);
		return `Poster · ${platform.charAt(0).toUpperCase()}${platform.slice(1)}`;
	}
	if (source.startsWith('social')) return 'Social listening';
	if (source === 'web') return 'Web';
	if (source === 'news' || source === 'gdelt') return 'News';
	return source.charAt(0).toUpperCase() + source.slice(1);
};

const compactCount = (value: number): string =>
	new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 }).format(value);

const cloneGroundingEvidenceList = (items: GroundingEvidence[] | undefined): GroundingEvidence[] =>
	(items || []).map((item) => ({ ...item }));

const cloneGroundingSnapshot = (
	snapshot: IdeateGroundingSnapshot | undefined
): IdeateGroundingSnapshot | undefined => {
	if (!snapshot) return undefined;
	return {
		framing: cloneGroundingEvidenceList(snapshot.framing),
		boardConcepts: cloneGroundingEvidenceList(snapshot.boardConcepts),
		likedResearch: cloneGroundingEvidenceList(snapshot.likedResearch),
		flowy: cloneGroundingEvidenceList(snapshot.flowy),
		socialProfiles: cloneGroundingEvidenceList(snapshot.socialProfiles),
		socialSegments: (snapshot.socialSegments || []).map((segment) => ({ ...segment })),
		selectedSocialSegmentIds: [...(snapshot.selectedSocialSegmentIds || [])],
		selectedFraming: cloneGroundingEvidenceList(snapshot.selectedFraming ?? snapshot.framing),
		selectedBoardConcepts: cloneGroundingEvidenceList(
			snapshot.selectedBoardConcepts ?? snapshot.boardConcepts
		),
		selectedLikedResearch: cloneGroundingEvidenceList(
			snapshot.selectedLikedResearch ?? snapshot.likedResearch
		),
		selectedSocialProfiles: cloneGroundingEvidenceList(
			snapshot.selectedSocialProfiles ?? snapshot.socialProfiles
		),
	};
};

const GROUNDING_NODE_RADIUS = Math.round(48 * 1.25);
const EXPANDABLE_TEXT_TOPIC = 'expandable-text';
// Rich social cards are 4 * nodeRadius wide and at most 270px tall. Keep the
// layout metrics here in sync with graphNodeUtils so deterministic placement
// leaves a real gutter instead of putting neighbouring card edges together.
const SOCIAL_LANDSCAPE_CARD_WIDTH = GROUNDING_NODE_RADIUS * 4;
const SOCIAL_LANDSCAPE_CARD_HEIGHT = 270;
const SOCIAL_LANDSCAPE_COLUMN_GAP = 36;
const SOCIAL_LANDSCAPE_ROW_GAP = 40;
const SOCIAL_LANDSCAPE_LANE_GAP = 80;
const SOCIAL_LANDSCAPE_HEADER_HEIGHT = 92;
const SOCIAL_LANDSCAPE_HEADER_GAP = 40;
const SOCIAL_LANDSCAPE_PANEL_PADDING_X = 50;
const SOCIAL_LANDSCAPE_PANEL_PADDING_BOTTOM = 40;

const groundingCatalogItems = (
	snapshot: IdeateGroundingSnapshot | undefined
): GroundingEvidence[] => [
	...(snapshot?.framing || []),
	...(snapshot?.boardConcepts || []),
	...(snapshot?.likedResearch || []),
	...(snapshot?.socialSegments || []).map(socialLandscapeSegmentEvidence),
	...(snapshot?.socialProfiles || []),
];

const shouldExpandGroundingEvidence = (
	item: GroundingEvidence | undefined,
	label: string | null | undefined
): boolean => {
	if (!item && !label) return false;
	const shown = (label || '').trim();
	if (shown.endsWith('…') || isLongNodeLabel(shown)) return true;
	if (!item) return false;
	const full = `${item.title}: ${item.detail}`.replace(/\s+/g, ' ').trim();
	return full.length > shown.length + 8 || item.detail.trim().length > 48;
};

type PositioningConfigurationState = {
	context: PositioningGenerationContext;
	groundingEvidenceCatalog: GroundingEvidence[];
	lane: PositioningLane;
	title: string;
};
const CLUSTER_Y_START = -CLUSTER_SPACING / 2;
const CLUSTER_X_START = -CLUSTER_SPACING;
const CLUSTER_PROPS: Array<ClusterProps> = [
	{
		x: CLUSTER_X_START,
		y: CLUSTER_Y_START,
		colourClass: 'green',
	},
	{
		x: CLUSTER_X_START,
		y: CLUSTER_Y_START + 2 * CLUSTER_SPACING,
		colourClass: 'red',
	},
	{
		x: CLUSTER_X_START + 1 * CLUSTER_SPACING,
		y: CLUSTER_Y_START + 1 * CLUSTER_SPACING,
		colourClass: 'fuchsia',
	},
	{
		x: CLUSTER_X_START + 2 * CLUSTER_SPACING,
		y: CLUSTER_Y_START,
		colourClass: 'orange',
	},
	{
		x: CLUSTER_X_START + 2 * CLUSTER_SPACING,
		y: CLUSTER_Y_START + 2 * CLUSTER_SPACING,
		colourClass: 'green',
	},
	{
		x: CLUSTER_X_START + 3 * CLUSTER_SPACING,
		y: CLUSTER_Y_START + 1 * CLUSTER_SPACING,
		colourClass: 'red',
	},
	{
		x: CLUSTER_X_START + 4 * CLUSTER_SPACING,
		y: CLUSTER_Y_START,
		colourClass: 'green',
	},
	{
		x: CLUSTER_X_START + 4 * CLUSTER_SPACING,
		y: CLUSTER_Y_START + 2 * CLUSTER_SPACING,
		colourClass: 'fuchsia',
	},
	{
		x: CLUSTER_X_START + 5 * CLUSTER_SPACING,
		y: CLUSTER_Y_START + 1 * CLUSTER_SPACING,
		colourClass: 'orange',
	},
];

class IdeateGraph extends Graph {
	public clusters: Array<NCCluster>;
	protected analyses: Array<AnalysisKey>;
	public activeAnalysis: AnalysisKey | undefined;
	protected inspireLabels: Array<string>;
	protected labelString: string;
	private socialLandscapePanelCenter: { x: number; y: number } | null = null;
	protected includeAiToolResults: boolean;
	protected input: string;
	protected vision: string;
	protected summary: string;
	protected pdfSummary: string;
	private positioningInsight = '';
	private positioningLayout: PositioningLayout = {};
	private positioningWorkspace: IdeateWorkspaceController | null = null;
	private positioningWorkspaceOptions: Parameters<typeof setupIdeateWorkspace>[0] | null = null;
	private positioningConfiguration: PositioningConfigurationState | null = null;

	constructor(
		graphData: NCGraphData,
		boardUsers: Array<BoardUser>,
		userSelections: Array<UserSelection>,
		colourClass: AvailableColour,
		labels: Array<string>,
		activeAnalysis: AnalysisKey | undefined,
		isCollaborative: boolean,
		automation: boolean,
		input: string,
		vision: string,
		summary: string,
		pdfSummary: string
	) {
		super(
			graphData,
			boardUsers,
			userSelections,
			colourClass,
			'#767680',
			isCollaborative,
			automation,
			function (this: IdeateGraph) {
				this.setClustersAndAnalyses(graphData);

				this.groups.clusters = createClusters(
					this.baseElement,
					this.clusters,
					this.enterCluster.bind(this),
					this.graphProps
				);
			}
		);
		// Panels are transient SVG chrome, rather than persisted graph data. Start
		// each Ideate graph with no panel; Grounding context recreates it explicitly.
		this.baseElement.select('g.social-landscape-panel').remove();
		this.socialLandscapePanelCenter = null;
		document.querySelector('#generate-container')?.classList.add('hidden');

		this.input = input;
		this.vision = vision;
		this.summary = summary;
		this.pdfSummary = pdfSummary;
		this.positioningInsight = graphData.positioningInsight || '';
		this.positioningLayout = graphData.positioningLayout || {};
		this.inspireLabels = labels;
		this.labelString = labels.join(' ');

		this.includeAiToolResults = document.querySelector<HTMLInputElement>(
			'input#ideate-use-ai-results'
		)!.checked;

		const lastActiveAnalysis = document.querySelector<HTMLLIElement>('.analyses-types li.clicked')
			?.dataset.analysis as AnalysisKey | undefined;
		if (lastActiveAnalysis) {
			// We switched between personal/team, so stay on the last selected analysis
			this.activeAnalysis = lastActiveAnalysis;
		}

		this.setupAnalysisList();

		const activeLi = this.getActiveLi(activeAnalysis);
		if (activeLi) {
			const analysesKey = activeLi.dataset.analysis as AnalysisKey;
			this.loadAnalysis(analysesKey, ANALYSES_TYPES[analysesKey], activeLi, false);
		}

		document.querySelector('#analyses-container')!.classList.remove('hidden');
		this.setupPositioningWorkspace();

		if (this.shouldShowRecommenderForm(true)) {
			// Show recommendation form on first load
			document.querySelector('#analyse-form-container')!.classList.remove('hidden');
			tutorialEvents.tutorialEmitter.emit(
				tutorialEvents.TUTORIAL_EVENT_TYPES.ideateRecommenderOpened,
				{
					engine: document.body.dataset.engine || '',
					vision: this.vision,
					pdfSummary: this.pdfSummary,
					inspireLabels: this.inspireLabels,
				}
			);
		}
	}

	getActiveLi(activeAnalysis: AnalysisKey | undefined): HTMLLIElement | null {
		let activeLi: HTMLLIElement | null = null;
		if (this.activeAnalysis) {
			activeLi = document.querySelector<HTMLLIElement>('.analyses-types li.clicked')!;
		}
		if (!activeLi && activeAnalysis && activeAnalysis !== this.activeAnalysis) {
			activeLi = document.querySelector<HTMLLIElement>(
				`.analyses-types li[data-analysis='${activeAnalysis}']`
			);
		}
		return activeLi;
	}

	shouldShowRecommenderForm(shouldShowAlert = false): boolean {
		// The positioning direction is the user's one-time decision. Analysis
		// selections can legitimately be empty or refreshed later, and must not
		// cause the initial direction overlay to reappear on a return to Ideate.
		const hasPositioningConfiguration = Boolean(this.positioningInsight.trim());
		if (
			!this.canConfigurePositioning() ||
			this.automation ||
			this.hasAcknowledgedCurrentWelcome() ||
			hasPositioningConfiguration
		) {
			return false;
		}
		return this.validate(shouldShowAlert);
	}

	canConfigurePositioning(): boolean {
		return document.body.dataset.boardOwner === 'true';
	}

	private hasAcknowledgedCurrentWelcome(): boolean {
		return this.currentUser.settings.ideateWelcomeVersion === IDEATE_WELCOME_RELEASE;
	}

	acknowledgeCurrentWelcome(): void {
		if (this.hasAcknowledgedCurrentWelcome()) return;
		this.currentUser.settings.ideateWelcomeVersion = IDEATE_WELCOME_RELEASE;
		this.saveUser();
	}

	validate(shouldShowAlert = false): boolean {
		if (this.hasPositioningSource()) return true;
		if (shouldShowAlert) {
			showAlert(this.getMissingIdeateSourceMessage());
		}
		return false;
	}

	refreshCurrentAnalysis(): Promise<boolean> {
		if (this.activeAnalysis) {
			const activeLi = document.querySelector<HTMLLIElement>('.analyses-types li.clicked')!;
			if (activeLi) {
				const analysesKey = activeLi.dataset.analysis as AnalysisKey;
				return this.loadAnalysis(analysesKey, ANALYSES_TYPES[analysesKey], activeLi, true);
			}
		}
		return Promise.resolve(false);
	}

	loadAnalysis(
		analysisKey: AnalysisKey,
		analysis: ClusterAnalysis,
		li: HTMLLIElement,
		isNew = false,
		clustersOverride?: NCCluster[]
	): Promise<boolean> {
		if (analysisKey === GROUNDING_CONTEXT_ANALYSIS_KEY) {
			return this.loadGroundingContextAnalysis(li, isNew);
		}
		// This SVG panel is an Explorer-only visual for Grounding context. It is
		// not represented by a persisted cluster, so deleting the old nodes alone
		// does not remove it when the user changes analysis (or returns to Ideate).
		this.baseElement.select('g.social-landscape-panel').remove();
		this.socialLandscapePanelCenter = null;

		const analysesList = document.querySelector('#analyses-container .analyses-types')!;

		for (const otherLi of analysesList.children) {
			otherLi.classList.remove('clicked');
		}
		li.classList.toggle('clicked');

		let inputStringForClusters = this.includeAiToolResults
			? `${this.labelString} ${this.userSelectionsToUseInAnalysis().join(' ')}`
			: this.labelString;

		if (!inputStringForClusters.trim()) {
			inputStringForClusters = this.currentUser.selectedFile?.fileSummary || this.vision || '';
		}

		if (!inputStringForClusters.trim()) {
			if (!this.automation) {
				showAlert(this.getMissingIdeateSourceMessage());
			}
			return Promise.resolve(false);
		}

		return new Promise<boolean>((resolve) => {
			const clustersPromise = clustersOverride
				? Promise.resolve(clustersOverride)
				: ncApi.clusters(this.boardId, analysisKey, inputStringForClusters);
			clustersPromise.then((clusters) => {
				console.log('[clusters]', clusters);

				if (clusters.length === 0) {
					if (!this.automation && this.validate()) {
						showAlert(
							`No clusters were generated by '${analysisKey}'. You may like to try another analysis type.`
						);
					}
					resolve(false);
				} else {
					this.capturePositioningAnalysisSnapshot(analysisKey, clusters);
					this.applyAnalysisClusters(analysisKey, clusters, isNew, resolve);
				}
			});
		});
	}

	private applyAnalysisClusters(
		analysisKey: AnalysisKey,
		clusters: NCCluster[],
		isNew: boolean,
		resolve: (loaded: boolean) => void
	) {
		if (neuroCreate.saveNodes && neuroCreate.saveLinks) {
			neuroCreate.saveLinks?.delete(this.links, () => {
				neuroCreate.saveNodes?.delete(this.nodes, () => {
					neuroCreate.saveClusters?.delete(this.clusters, () => {
						this.activeAnalysis = analysisKey;
						console.log('[clusters else working]');
						const isGrounding = analysisKey === GROUNDING_CONTEXT_ANALYSIS_KEY;
						const groundingSnapshot =
							this.positioningConfiguration?.context.groundingSnapshot;
						const groundingLayouts = isGrounding
							? layoutGroundingContextClusters(clusters)
							: [];

						clusters.forEach((cluster, index) => {
							console.log('[forEach]', cluster, index);

							if (index < CLUSTER_PROPS.length) {
								const groundingLayout = groundingLayouts[index];
								if (groundingLayout) {
									cluster.radius = groundingLayout.circleRadius;
									cluster.layout = {
										x: groundingLayout.x,
										y: groundingLayout.y,
										orbitRadius: groundingLayout.orbitRadius,
									};
								}
								const centralNode = this.spawnCluster(cluster, {
									x: groundingLayout?.x ?? CLUSTER_PROPS[index].x,
									y: groundingLayout?.y ?? CLUSTER_PROPS[index].y,
									colourClass: this.isCollaborative ? 'blue' : this.userColourClass,
									...(isGrounding
										? {
												radius: groundingLayout?.orbitRadius,
												styleLongAsRect: true,
											markExpandableText: true,
											skipLinks: isGrounding && cluster.group === 'Social landscape',
											}
										: {}),
								});
								if (isGrounding) {
									if (cluster.group === 'Social landscape') {
										this.spawnSocialLandscapePostSubclusters(centralNode, groundingSnapshot);
									}
									this.annotateGroundingExpandableNodes(cluster, groundingSnapshot);
								}
								if (neuroCreate.saveClusters) {
									// Social landscape is a structured panel, not a graph cluster:
									// omit the inherited dotted ring and hub-and-spoke links.
									if (!(isGrounding && cluster.group === 'Social landscape')) {
										neuroCreate.saveClusters.create([cluster]);
									}
								}
							}
						});
						this.save(() => {
							void this.zoomToFit(this.automation).then(() => {
								if (isGrounding && this.socialLandscapePanelCenter) {
									this.centerTo(this.socialLandscapePanelCenter);
								}
							});
							// Grounding has a deterministic cluster layout and rich media cards.
							// Leaving the general force simulation running makes the explorer
							// continuously repaint and undermines that layout.
							if (isGrounding) {
								this.simulation?.alpha(0).stop();
							}
							if (isNew) {
								const analysisName = ANALYSES_TYPES[analysisKey].name;
								tutorialEvents.loadedNewAnalysis(analysisName);
							}
							resolve(true);
						});
					});
				});
			});
		} else {
			resolve(false);
		}
	}

	private annotateGroundingExpandableNodes(
		cluster: NCCluster,
		snapshot: IdeateGroundingSnapshot | undefined
	) {
		const catalog = groundingCatalogItems(snapshot);
		const updated: NCNode[] = [];
		this.nodes.forEach((node) => {
			const belongsToCluster =
				node.parent?.label === cluster.group ||
				(cluster.group === 'Social landscape' &&
					(snapshot?.socialSegments || []).some((segment) => segment.label === node.parent?.label));
			if (!belongsToCluster || !node.label) return;
			let changed = false;
			// Grounding selection is conveyed by the yellow selected state. Do not
			// retain the inherited IDEATE fill, which made deselected nodes look selected.
			if (node.nodeColour) {
				delete node.nodeColour;
				changed = true;
			}
			const item = findGroundingEvidenceByNodeLabel(catalog, node.label);
			if (item) {
				node.groundingEvidence = { ...item };
				changed ||= node.style !== 'rect';
				node.style = 'rect';
				if (item.originBoard === 'inspire') {
					node.nodeColour = 'GREEN';
					changed = true;
				}
			}
			if (shouldExpandGroundingEvidence(item, node.label)) {
				if (isLongNodeLabel(node.label) || (node.label || '').endsWith('…')) {
					changed ||= node.style !== 'rect';
					node.style = 'rect';
				}
				if (!node.topics?.includes(EXPANDABLE_TEXT_TOPIC)) {
					node.topics = [...(node.topics || []), EXPANDABLE_TEXT_TOPIC];
					changed = true;
				}
			}
			if (changed) updated.push(node);
		});
		if (updated.length && neuroCreate.saveNodes?.update) {
			neuroCreate.saveNodes.update(updated);
		}
	}

	private spawnSocialLandscapePostSubclusters(
		landscapeNode: NCNode,
		snapshot: IdeateGroundingSnapshot | undefined
	) {
		if (!snapshot) return;
		const subclusters = buildSocialLandscapeSubclusters(
			(snapshot.socialProfiles || []).slice(0, 12),
			snapshot.socialSegments || []
		);
		landscapeNode.style = 'rect';
		const laneWidth = SOCIAL_LANDSCAPE_CARD_WIDTH * 2 + SOCIAL_LANDSCAPE_COLUMN_GAP;
		const lanePitch = laneWidth + SOCIAL_LANDSCAPE_LANE_GAP;
		const laneStartX = landscapeNode.x - ((subclusters.length - 1) * lanePitch) / 2;
		const firstRowOffset =
			SOCIAL_LANDSCAPE_HEADER_HEIGHT / 2 +
			SOCIAL_LANDSCAPE_HEADER_GAP +
			SOCIAL_LANDSCAPE_CARD_HEIGHT / 2;
		const columnOffset =
			SOCIAL_LANDSCAPE_CARD_WIDTH / 2 + SOCIAL_LANDSCAPE_COLUMN_GAP / 2;
		const rowPitch = SOCIAL_LANDSCAPE_CARD_HEIGHT + SOCIAL_LANDSCAPE_ROW_GAP;
		const panelNodes: NCNode[] = [];
		for (const [laneIndex, { segment, profiles }] of subclusters.entries()) {
			const segmentNode = this.nodes.find(
				(node) => node.parent?.uid === landscapeNode.uid && node.label === segment.label
			);
			if (!segmentNode || !profiles.length) continue;
			// The segment is a lane header, not another radial centre. Persist the
			// deterministic location before placing its cards in a fixed grid below.
			segmentNode.x = laneStartX + laneIndex * lanePitch;
			segmentNode.y = landscapeNode.y - 120;
			segmentNode.style = 'rect';
			this.spawnCluster(
				{ group: segment.label, values: profiles.map(groundingEvidenceNodeLabel) },
				{
					x: segmentNode.x,
					y: segmentNode.y + firstRowOffset,
					colourClass: this.isCollaborative ? 'blue' : this.userColourClass,
					radius: 1,
					styleLongAsRect: true,
					existingCentralNode: segmentNode,
					skipLinks: true,
					mutateLinkedNode: (node) => {
						const evidence = findGroundingEvidenceByNodeLabel(
							snapshot.socialProfiles,
							node.label || ''
						);
						if (!evidence) return;
						node.groundingEvidence = { ...evidence };
						node.style = 'rect';
						// Rich social cards have their own single header expand action.
						node.topics = (node.topics || []).filter(
							(topic) => topic !== EXPANDABLE_TEXT_TOPIC
						);
					},
				}
			);
			const lanePosts = this.nodes.filter((node) => node.parent?.uid === segmentNode.uid);
			lanePosts.forEach((post, index) => {
				const column = index % 2;
				const row = Math.floor(index / 2);
				post.x = segmentNode.x + (column === 0 ? -columnOffset : columnOffset);
				post.y = segmentNode.y + firstRowOffset + row * rowPitch;
			});
			panelNodes.push(segmentNode, ...lanePosts);
			neuroCreate.saveNodes?.update?.([segmentNode, ...lanePosts]);
		}
		// The layout anchor exists only to preserve parent relationships. It is not
		// part of the interaction model: users select a lane or an individual post.
		landscapeNode.hidden = true;
		neuroCreate.saveNodes?.update?.([landscapeNode]);
		this.renderSocialLandscapePanel(panelNodes);
	}

	private renderSocialLandscapePanel(nodes: NCNode[]) {
		this.baseElement.select('g.social-landscape-panel').remove();
		if (!nodes.length) {
			this.socialLandscapePanelCenter = null;
			return;
		}
		const horizontalPanelInset =
			SOCIAL_LANDSCAPE_CARD_WIDTH / 2 + SOCIAL_LANDSCAPE_PANEL_PADDING_X;
		const minX = Math.min(...nodes.map((node) => node.x)) - horizontalPanelInset;
		const maxX = Math.max(...nodes.map((node) => node.x)) + horizontalPanelInset;
		const minY = Math.min(...nodes.map((node) => node.y)) - 180;
		const maxY =
			Math.max(...nodes.map((node) => node.y)) +
			SOCIAL_LANDSCAPE_CARD_HEIGHT / 2 +
			SOCIAL_LANDSCAPE_PANEL_PADDING_BOTTOM;
		const panel = this.baseElement
			.insert('g', '.links')
			.attr('class', 'social-landscape-panel')
			.style('pointer-events', 'none');
		this.socialLandscapePanelCenter = { x: (minX + maxX) / 2, y: (minY + maxY) / 2 };
		panel
			.append('rect')
			.attr('x', minX)
			.attr('y', minY)
			.attr('width', maxX - minX)
			.attr('height', maxY - minY)
			.attr('rx', 28)
			.attr('fill', 'rgba(242, 246, 255, 0.72)')
			.attr('stroke', '#a8b9dd')
			.attr('stroke-width', 2);
		panel
			.append('text')
			.attr('x', minX + 24)
			.attr('y', minY + 38)
			.attr('fill', '#314a8e')
			.attr('font-size', 18)
			.attr('font-weight', 800)
			.text('Social landscape');
		panel
			.append('text')
			.attr('x', minX + 24)
			.attr('y', minY + 60)
			.attr('fill', '#62709a')
			.attr('font-size', 12)
			.attr('font-weight', 600)
			.text('Audience voices grouped by stance');
	}

	protected suppressBlankCanvasSelectionAlert(): boolean {
		return Boolean(this.activeAnalysis);
	}

	get nodeRadius(): number {
		return this.activeAnalysis === GROUNDING_CONTEXT_ANALYSIS_KEY
			? GROUNDING_NODE_RADIUS
			: super.nodeRadius;
	}

	protected openNodeContent(node: NCNode): void {
		if (
			this.activeAnalysis === GROUNDING_CONTEXT_ANALYSIS_KEY &&
			(node.groundingEvidence?.source.startsWith('social-profile:') ||
				node.topics?.includes(EXPANDABLE_TEXT_TOPIC))
		) {
			this.openGroundingNodeContent(node);
			return;
		}
		super.openNodeContent(node);
	}

	private openGroundingNodeContent(node: NCNode): void {
		const snapshot = this.positioningConfiguration?.context.groundingSnapshot;
		const item = findGroundingEvidenceByNodeLabel(groundingCatalogItems(snapshot), node.label || '');
		const modalContent = document.createElement('article');
		modalContent.className = 'pdf-summary-modal pdf-summary-modal--grounding';
		const isSocialPost = Boolean(item?.source.startsWith('social-profile:'));

		const eyebrow = document.createElement('p');
		eyebrow.className = 'pdf-summary-modal__eyebrow';
		eyebrow.textContent = node.parent?.label || 'Grounding context';

		const title = document.createElement('h2');
		title.className = 'pdf-summary-modal__title';
		title.textContent = item?.title || node.label || 'Grounding signal';

		const socialMeta = document.createElement('div');
		socialMeta.className = 'grounding-social-meta';
		if (isSocialPost && item?.author) {
			const author = document.createElement('span');
			author.textContent = item.author;
			socialMeta.append(author);
		}
		if (isSocialPost && typeof item?.engagement?.likes === 'number') {
			const likes = document.createElement('span');
			likes.textContent = `${compactCount(item.engagement.likes)} like${item.engagement.likes === 1 ? '' : 's'}`;
			socialMeta.append(likes);
		}

		let media: HTMLElement | undefined;
		if (isSocialPost && item?.image) {
			media = document.createElement('figure');
			media.className = 'grounding-social-media';
			const image = document.createElement('img');
			image.src = item.image;
			image.alt = item.isVideo ? 'Video preview from the social post' : 'Image from the social post';
			image.loading = 'lazy';
			image.referrerPolicy = 'no-referrer';
			image.addEventListener('error', () => media?.remove());
			media.append(image);
			if (item.isVideo) {
				const videoBadge = document.createElement('span');
				videoBadge.className = 'grounding-social-media__video';
				videoBadge.textContent = 'Video';
				media.append(videoBadge);
			}
		}

		const summaryHeading = document.createElement('h3');
		summaryHeading.className = 'pdf-summary-modal__summary-heading';
		summaryHeading.textContent = isSocialPost ? 'Full social post' : 'Detail';

		const summary = document.createElement('p');
		summary.className = 'pdf-summary-modal__summary';
		summary.textContent =
			item?.content || item?.detail || node.label || 'No additional detail available.';

		modalContent.append(eyebrow, title);
		if (socialMeta.childElementCount) modalContent.append(socialMeta);
		if (media) modalContent.append(media);
		modalContent.append(summaryHeading, summary);
		if (item?.url) {
			const sourceSection = document.createElement('section');
			sourceSection.className = 'pdf-summary-modal__source';
			const linkHeading = document.createElement('h3');
			linkHeading.className = 'pdf-summary-modal__summary-heading';
			linkHeading.textContent = 'Source';
			const link = document.createElement('a');
			link.className = 'pdf-summary-modal__source-link';
			link.href = item.url;
			link.target = '_blank';
			link.rel = 'noopener noreferrer';
			link.textContent = item.url;
			sourceSection.append(linkHeading, link);
			modalContent.append(sourceSection);
		}
		createDialog('', [modalContent], createButtons([{ label: 'Close', primary: true }], true));
	}

	private loadGroundingContextAnalysis(li: HTMLLIElement, isNew = false): Promise<boolean> {
		const snapshot = this.positioningConfiguration?.context.groundingSnapshot;
		const clusters = buildGroundingContextClusters(snapshot);
		const analysesList = document.querySelector('#analyses-container .analyses-types')!;
		for (const otherLi of analysesList.children) {
			otherLi.classList.remove('clicked');
		}
		li.classList.add('clicked');

		if (!clusters.length) {
			if (!this.automation) {
				showAlert(
					'No grounding context was captured for this output yet. Regenerate it to capture board, Idea Box, framing, and poster profiles.'
				);
			}
			return Promise.resolve(false);
		}

		return new Promise<boolean>((resolve) => {
			this.applyAnalysisClusters(GROUNDING_CONTEXT_ANALYSIS_KEY, clusters, isNew, (loaded) => {
				if (loaded) this.syncGroundingContextSelection();
				resolve(loaded);
			});
		});
	}

	finishedRecommenderForm() {
		if (!this.canConfigurePositioning()) return;
		const selectedAnalyses = Array.from(
			document.querySelectorAll<HTMLInputElement>(
				'#analyse-form-container .form-recommend fieldset input:checked'
			)
		).map((input) => input.value as AnalysisKey);
		if (selectedAnalyses.length > 0) this.analyses = selectedAnalyses;
		this.positioningInsight =
			document.querySelector<HTMLInputElement>('#analysesForm .first-question input:checked')
				?.labels?.[0]?.dataset.insight || this.positioningInsight;
		this.setupAnalysisList();
		this.setupPositioningWorkspace();
		if (document.body.dataset.showTutorial === 'true') {
			this.startTutorial();
		} else {
			this.startHints();
		}
	}

	private setupPositioningWorkspace() {
		this.positioningWorkspace?.destroy();
		const display = this.getIdeateContextDisplay();
		this.positioningWorkspaceOptions = {
			vision: display.vision,
			insight: this.positioningInsight,
			pdfSummary: display.pdfSummary,
			pdfTopics: display.pdfTopics,
			inspireLabels: this.inspireLabels,
			analyses: this.getPositioningAnalyses(),
			isTeam: this.isCollaborative,
			canConfigurePositioning: this.canConfigurePositioning(),
			canvas: this.baseSvgGroup,
			onZoomToFit: () => {
				void this.zoomToFit(this.automation);
			},
			onCenterMap: () => {
				const problemCenter = this.positioningWorkspace?.getProblemCenter();
				if (problemCenter) this.centerTo(problemCenter);
			},
			onAddOutput: (lane) => {
				if (!this.hasPositioningSource()) {
					showAlert(this.getMissingIdeateSourceMessage());
					return;
				}
				const taskByLane = {
					audience: { task: 'persona', label: 'Audience & personas' },
					motivation: { task: 'motivations', label: 'Stakeholder motivations' },
					solution: { task: 'brainstorm', label: 'Solutions & opportunities' },
					brand: { task: 'brand-tone', label: 'Brand tone of voice' },
					story: { task: 'story', label: 'Story & characters' },
				} as const;
				const generation = taskByLane[lane];
				window.dispatchEvent(
					new CustomEvent('positioning-generate-request', {
						detail: {
							task: generation.task,
							analyses: this.getPositioningAnalyses(),
							sourceContext: this.getPositioningSourceContext(),
							contextLabel: generation.label,
						},
					})
				);
			},
			onConfigureOutput: (context, lane, title) => {
				this.openPositioningConfiguration(context, lane, title);
			},
			onShowAnalysis: () => this.setZoomScaleExtent(0),
			onShowOverview: () => {
				// Grounding Context's social landscape is transient explorer chrome.
				// Returning to the positioning map does not recreate the graph, so
				// remove it explicitly instead of allowing it to sit behind the map.
				this.baseElement.select('g.social-landscape-panel').remove();
				this.socialLandscapePanelCenter = null;
				this.setZoomScaleExtent(POSITIONING_MAP_MIN_ZOOM);
				this.closePositioningConfiguration();
				this.syncPositioningZoomReadability(this.zoomTransform?.k || 1);
			},
			initialLayout: this.positioningLayout,
			onLayoutChange: (layout) => {
				this.positioningLayout = layout;
				globalThis.neuroCreate.savePositioningLayout?.(layout);
			},
		};
		this.positioningWorkspace = setupIdeateWorkspace(this.positioningWorkspaceOptions);
		void this.hydrateIdeateContextFromPdfAnalyses();
	}

	private getContextPdfFiles(): UserFile[] {
		return resolvePdfContextFiles(
			{ _id: this.boardId, uploadedFiles: this.uploadedFiles || [] },
			this.currentUser
		);
	}

	private getIdeateContextDisplay() {
		return resolveIdeateContextCopy({
			vision: this.vision,
			summary: this.summary,
			input: this.input,
			boardPdfSummary: this.pdfSummary,
			files: this.getContextPdfFiles(),
		});
	}

	private applyIdeateContextDisplay(display: {
		vision: string;
		pdfSummary: string;
		pdfTopics: string[];
	}) {
		if (!this.vision && display.vision) this.vision = display.vision;
		if (!this.pdfSummary && display.pdfSummary) this.pdfSummary = display.pdfSummary;
		if (this.positioningWorkspaceOptions) {
			this.positioningWorkspaceOptions.vision = display.vision;
			this.positioningWorkspaceOptions.pdfSummary = display.pdfSummary;
			this.positioningWorkspaceOptions.pdfTopics = display.pdfTopics;
		}
	}

	private async hydrateIdeateContextFromPdfAnalyses() {
		try {
			const { sources, context } = await ncApi.getFilesOfBoard(this.boardId);
			if (!this.positioningWorkspaceOptions) return;
			const refs = context.length
				? context
				: sources
						.filter((source) => source.currentBoard)
						.map((source) => ({ boardId: source.boardId, fileId: source.file.fileId }));
			const relevant = sources.filter((source) =>
				refs.some((ref) => ref.boardId === source.boardId && ref.fileId === source.file.fileId)
			);
			if (!relevant.length) return;
			const copy = resolveIdeateContextCopy({
				vision: this.vision,
				summary: this.summary,
				input: this.input,
				boardPdfSummary: this.pdfSummary,
				files: relevant.map((source) => ({
					fileName: source.file.fileName,
					fileSummary: (source.analysis?.overview || source.file.fileSummary || '').trim(),
					topics: [
						...(source.file.topics || []),
						...(source.analysis?.keywords || []).map((topic) => ({ topic })),
					],
				})),
			});
			this.applyIdeateContextDisplay(copy);
			await this.positioningWorkspace?.refresh();
		} catch (error) {
			console.error('Unable to hydrate Ideate context from PDF analyses', error);
		}
	}

	protected onZoomScaleChanged(scale: number) {
		this.syncPositioningZoomReadability(scale);
	}

	private syncPositioningZoomReadability(scale: number) {
		const positioningMap = this.baseSvgGroup.querySelector<SVGGElement>(
			'.positioning-map-canvas'
		);
		if (!positioningMap) return;
		positioningMap.classList.toggle(
			'positioning-map-canvas--overview',
			scale < POSITIONING_MAP_OVERVIEW_ZOOM
		);
		positioningMap.classList.toggle(
			'positioning-map-canvas--far-overview',
			scale < POSITIONING_MAP_FAR_OVERVIEW_ZOOM
		);
	}

	private getPositioningAnalyses(): AnalysisKey[] {
		if (this.analyses?.length) return this.analyses;
		const inherited = resolveIdeatePositioning(globalThis.neuroCreate.doc?.data).analyses;
		return analysesForPositioningGenerate(inherited);
	}

	private getPositioningSourceContext(): PositioningSourceContext {
		const display = this.getIdeateContextDisplay();
		const seenTopics = new Set<string>();
		const pdfTopics: Array<{ topic: string; extractedText: string }> = [];
		const addTopic = (topic: string, extractedText = '') => {
			const value = topic.trim();
			const key = value.toLocaleLowerCase();
			if (!value || seenTopics.has(key)) return;
			seenTopics.add(key);
			pdfTopics.push({ topic: value, extractedText });
		};
		for (const file of this.getContextPdfFiles()) {
			for (const entry of file.topics || []) {
				addTopic(entry.topic, (entry.extracted_text || '').trim());
			}
		}
		for (const topic of this.positioningWorkspaceOptions?.pdfTopics || display.pdfTopics) {
			addTopic(topic);
		}
		const seenAnalysisTerms = new Set<string>();
		const analysisTerms = this.inspireLabels.filter((term) => {
			const key = term.trim().toLocaleLowerCase();
			if (!key || seenAnalysisTerms.has(key)) return false;
			seenAnalysisTerms.add(key);
			return true;
		});
		return {
			projectFraming:
				[
					this.positioningWorkspaceOptions?.vision,
					display.vision,
					this.vision,
					this.summary,
					this.input,
				]
					.map((value) => (value || '').trim())
					.find(Boolean) || '',
			pdfOverview:
				(this.positioningWorkspaceOptions?.pdfSummary || display.pdfSummary || '').trim() ||
				undefined,
			pdfTopics,
			analysisTerms,
		};
	}

	private hasPositioningSource(): boolean {
		return hasPositioningSourceContext(this.getPositioningSourceContext());
	}

	private getMissingIdeateSourceMessage(): string {
		return missingIdeateSourceMessage(this.isCollaborative);
	}

	private openPositioningConfiguration(
		context: PositioningGenerationContext,
		lane: PositioningLane,
		title: string
	) {
		document.querySelector('#generate-container')?.classList.add('hidden');
		document.querySelector('#analyses-container')?.classList.add('positioning-config-active');
		const groundingEvidence = (context.groundingEvidence || []).map((evidence) => ({
			...evidence,
		}));
		this.positioningConfiguration = {
			context: {
				...context,
				sources: context.sources.map((source) => ({
					...ensurePositioningClusterCenter(source),
				})),
				analysisSnapshots: context.analysisSnapshots?.map((snapshot) => ({
					analysis: snapshot.analysis,
					clusters: snapshot.clusters.map((cluster) => ({
						...cluster,
						values: [...cluster.values],
					})),
				})),
				groundingEvidence: groundingEvidence.map((evidence) => ({ ...evidence })),
				groundingSnapshot: cloneGroundingSnapshot(context.groundingSnapshot),
			},
			groundingEvidenceCatalog: groundingEvidence,
			lane,
			title,
		};
		this.setupAnalysisList();
		this.renderPositioningConfiguration();
		const groundingLi = document.querySelector<HTMLLIElement>(
			`#analyses-container .analyses-types li[data-analysis='${GROUNDING_CONTEXT_ANALYSIS_KEY}']`
		);
		const firstAnalysis = groundingLi
			? GROUNDING_CONTEXT_ANALYSIS_KEY
			: this.positioningConfiguration.context.sources[0]?.analysis;
		const analysisLi = groundingLi || (firstAnalysis
			? document.querySelector<HTMLLIElement>(
					`#analyses-container .analyses-types li[data-analysis='${firstAnalysis}']`
				)
			: null);
		if (firstAnalysis && analysisLi) {
			void this.loadPositioningAnalysis(firstAnalysis, ANALYSES_TYPES[firstAnalysis], analysisLi);
		}
	}

	private capturePositioningAnalysisSnapshot(analysis: AnalysisKey, clusters: NCCluster[]) {
		if (analysis === GROUNDING_CONTEXT_ANALYSIS_KEY) return;
		const context = this.positioningConfiguration?.context;
		if (!context) return;
		context.analysisSnapshots ||= [];
		const snapshot = {
			analysis,
			clusters: clusters.map((cluster) => ({
				group: cluster.group,
				values: [...cluster.values],
			})),
		};
		const existingIndex = context.analysisSnapshots.findIndex(
			(candidate) => candidate.analysis === analysis
		);
		if (existingIndex >= 0) context.analysisSnapshots[existingIndex] = snapshot;
		else context.analysisSnapshots.push(snapshot);
	}

	private async loadPositioningAnalysis(
		analysisKey: AnalysisKey,
		analysis: ClusterAnalysis,
		li: HTMLLIElement,
		isNew = false
	) {
		if (analysisKey === GROUNDING_CONTEXT_ANALYSIS_KEY) {
			return this.loadGroundingContextAnalysis(li, isNew);
		}
		const snapshot = this.positioningConfiguration?.context.analysisSnapshots?.find(
			(candidate) => candidate.analysis === analysisKey
		);
		const loaded = await this.loadAnalysis(analysisKey, analysis, li, isNew, snapshot?.clusters);
		if (loaded) this.syncPositioningSelection();
		return loaded;
	}

	private closePositioningConfiguration() {
		if (!this.positioningConfiguration) return;
		this.positioningConfiguration = null;
		document.querySelector('#positioning-analysis-config')?.classList.add('hidden');
		const analysesContainer = document.querySelector('#analyses-container');
		analysesContainer?.classList.remove('positioning-config-active');
		const analysesList = analysesContainer?.querySelector('.analyses-types');
		const listAnchor = analysesContainer?.querySelector('#analyses-list-anchor');
		if (analysesList && listAnchor) listAnchor.after(analysesList);
		this.stopSelectingNodes('positioning-configuration');
		this.groups.clusters?.classed('positioning-selected', false);
		this.setupAnalysisList();
	}

	private syncPositioningSelection() {
		if (!this.positioningConfiguration || !this.activeAnalysis) return;
		if (this.activeAnalysis === GROUNDING_CONTEXT_ANALYSIS_KEY) {
			this.syncGroundingContextSelection();
			return;
		}
		const sources = this.positioningConfiguration.context.sources.filter(
			(source) => source.analysis === this.activeAnalysis
		);
		const selectedLabels = new Set(sources.flatMap((source) => source.nodes));
		const selectedNodes = this.nodes.filter(
			(node) => Boolean(node.label) && selectedLabels.has(node.label || '')
		);
		this.startSelectingNodes('positioningNodes', selectedNodes, 'positioning-configuration');
		const selectedClusters = new Set(sources.map((source) => source.cluster));
		this.groups.clusters?.classed('positioning-selected', (cluster) =>
			selectedClusters.has(cluster.group)
		);
		this.onTick();
	}

	private syncGroundingContextSelection() {
		if (!this.positioningConfiguration) return;
		const selectedKeys = selectedGroundingContextNodeKeys(
			this.positioningConfiguration.context.groundingSnapshot
		);
		const selectedNodes = this.nodes.filter(
			(node) =>
				Boolean(node.parent?.label && node.label) &&
				selectedKeys.has(groundingContextNodeKey(node.parent!.label, node.label || ''))
		);
		this.startSelectingNodes('positioningNodes', selectedNodes, 'positioning-configuration');
		this.groups.clusters?.classed('positioning-selected', (cluster) =>
			cluster.values.some((value) =>
				selectedKeys.has(groundingContextNodeKey(cluster.group, value))
			)
		);
		this.onTick();
	}

	private ensureGroundingSnapshot(): IdeateGroundingSnapshot {
		const configuration = this.positioningConfiguration!;
		configuration.context.groundingSnapshot ||= {
			boardConcepts: [],
			likedResearch: [],
			socialProfiles: [],
			socialSegments: [],
			selectedSocialSegmentIds: [],
			selectedFraming: [],
			selectedBoardConcepts: [],
			selectedLikedResearch: [],
			selectedSocialProfiles: [],
		};
		const snapshot = configuration.context.groundingSnapshot;
		if (!snapshot.socialSegments?.length && snapshot.socialProfiles?.length) {
			snapshot.socialSegments = buildSocialLandscapeSegments(snapshot.socialProfiles);
		}
		return snapshot;
	}

	private applySelectedSocialSegments(snapshot: IdeateGroundingSnapshot) {
		const selected = new Set(snapshot.selectedSocialSegmentIds || []);
		const evidenceIds = new Set(
			(snapshot.socialSegments || [])
				.filter((segment) => selected.has(segment.id))
				.flatMap((segment) => segment.evidenceIds)
		);
		snapshot.selectedSocialProfiles = (snapshot.socialProfiles || [])
			.filter((profile) => {
				const key = profile.socialEvidenceId || `${profile.source}:${profile.url || profile.title}`;
				return evidenceIds.has(key);
			})
			.map((profile) => ({ ...profile }));
	}

	private toggleGroundingContextNode(node: NCNode) {
		const configuration = this.positioningConfiguration;
		if (!configuration || !node.label) return;

		const clusterName = node.parent?.label || node.label;
		const snapshot = this.ensureGroundingSnapshot();
		const label = node.label;

		if (!node.parent) {
			// Cluster centre click: select/deselect all child nodes in that category.
			const childLabels = this.nodes
				.filter((candidate) => candidate.parent?.uid === node.uid && candidate.label)
				.map((candidate) => candidate.label!);
			const selectedKeys = selectedGroundingContextNodeKeys(snapshot);
			const selectAll = !childLabels.every((child) =>
				selectedKeys.has(groundingContextNodeKey(clusterName, child))
			);
			if (clusterName === 'Project framing') {
				const catalog = snapshot.framing || [];
				snapshot.selectedFraming = selectAll ? catalog.map((item) => ({ ...item })) : [];
			} else if (clusterName === 'Board concepts') {
				const catalog = snapshot.boardConcepts || [];
				snapshot.selectedBoardConcepts = selectAll
					? catalog.map((item) => ({ ...item }))
					: [];
			} else if (clusterName === 'Idea Box') {
				const catalog = snapshot.likedResearch || [];
				snapshot.selectedLikedResearch = selectAll
					? catalog.map((item) => ({ ...item }))
					: [];
			} else if (clusterName === 'Social landscape') {
				const catalog = snapshot.socialSegments || [];
				snapshot.selectedSocialSegmentIds = selectAll
					? catalog.map((segment) => segment.id)
					: [];
				this.applySelectedSocialSegments(snapshot);
				this.syncSocialProfilesIntoGroundingEvidence();
			} else if (clusterName === 'Social posters') {
				const catalog = snapshot.socialProfiles || [];
				snapshot.selectedSocialProfiles = selectAll
					? catalog.map((item) => ({ ...item }))
					: [];
				this.syncSocialProfilesIntoGroundingEvidence();
			}
			this.renderPositioningConfiguration();
			this.syncGroundingContextSelection();
			return;
		}

		if (clusterName === 'Project framing') {
			const catalog = snapshot.framing || [];
			const item = findGroundingEvidenceByNodeLabel(catalog, label);
			if (!item) return;
			const current = snapshot.selectedFraming ?? cloneGroundingEvidenceList(catalog);
			const key = groundingEvidenceKey(item);
			const selected = current.some((candidate) => groundingEvidenceKey(candidate) === key);
			snapshot.selectedFraming = selected
				? current.filter((candidate) => groundingEvidenceKey(candidate) !== key)
				: [...current, { ...item }];
		} else if (clusterName === 'Board concepts') {
			const catalog = snapshot.boardConcepts || [];
			const item = findGroundingEvidenceByNodeLabel(catalog, label);
			if (!item) return;
			const current = snapshot.selectedBoardConcepts ?? cloneGroundingEvidenceList(catalog);
			const key = groundingEvidenceKey(item);
			const selected = current.some((candidate) => groundingEvidenceKey(candidate) === key);
			snapshot.selectedBoardConcepts = selected
				? current.filter((candidate) => groundingEvidenceKey(candidate) !== key)
				: [...current, { ...item }];
		} else if (clusterName === 'Idea Box') {
			const catalog = snapshot.likedResearch || [];
			const item = findGroundingEvidenceByNodeLabel(catalog, label);
			if (!item) return;
			const current = snapshot.selectedLikedResearch ?? cloneGroundingEvidenceList(catalog);
			const key = groundingEvidenceKey(item);
			const selected = current.some((candidate) => groundingEvidenceKey(candidate) === key);
			snapshot.selectedLikedResearch = selected
				? current.filter((candidate) => groundingEvidenceKey(candidate) !== key)
				: [...current, { ...item }];
		} else if (clusterName === 'Social landscape') {
			const segment = (snapshot.socialSegments || []).find((candidate) => candidate.label === label);
			if (!segment) return;
			const current = new Set(snapshot.selectedSocialSegmentIds || []);
			if (current.has(segment.id)) current.delete(segment.id);
			else current.add(segment.id);
			snapshot.selectedSocialSegmentIds = [...current];
			this.applySelectedSocialSegments(snapshot);
			this.syncSocialProfilesIntoGroundingEvidence();
		} else if ((snapshot.socialSegments || []).some((segment) => segment.label === clusterName)) {
			const catalog = snapshot.socialProfiles || [];
			const item = findGroundingEvidenceByNodeLabel(catalog, label);
			if (!item) return;
			const current = snapshot.selectedSocialProfiles ?? cloneGroundingEvidenceList(catalog);
			const key = groundingEvidenceKey(item);
			const selected = current.some((candidate) => groundingEvidenceKey(candidate) === key);
			snapshot.selectedSocialProfiles = selected
				? current.filter((candidate) => groundingEvidenceKey(candidate) !== key)
				: [...current, { ...item }];
			this.syncSocialProfilesIntoGroundingEvidence();
		}

		this.renderPositioningConfiguration();
		this.syncGroundingContextSelection();
	}

	private syncSocialProfilesIntoGroundingEvidence() {
		const configuration = this.positioningConfiguration;
		if (!configuration) return;
		const snapshot = configuration.context.groundingSnapshot;
		const profiles =
			snapshot?.selectedSocialProfiles ?? snapshot?.socialProfiles ?? [];
		const nonProfile = (configuration.context.groundingEvidence || []).filter(
			(candidate) => !candidate.source.startsWith('social-profile:')
		);
		configuration.context.groundingEvidence = [
			...nonProfile,
			...profiles.map((profile) => ({ ...profile })),
		];
	}

	private renderPositioningConfiguration() {
		const configuration = this.positioningConfiguration;
		const panel = document.querySelector<HTMLElement>('#positioning-analysis-config');
		if (!configuration || !panel) return;
		panel.classList.remove('hidden');
		const analysesContainer = document.querySelector<HTMLElement>('#analyses-container');
		analysesContainer?.classList.add('positioning-config-active');
		const analysesList = analysesContainer?.querySelector('.analyses-types');
		const regenerate = panel.querySelector<HTMLButtonElement>('.positioning-config-regenerate')!;
		if (analysesList) regenerate.before(analysesList);
		const title = panel.querySelector<HTMLElement>('#positioning-analysis-config-title');
		if (title) title.innerText = `Psychographic basis for ${configuration.title}`;
		this.renderPositioningGroundingSelection(panel, configuration);
		this.renderPositioningAnalysisSelections();
		regenerate.disabled = configuration.context.sources.length === 0;
		regenerate.onclick = () => {
			const configuredContext = configuration.context;
			const analyses = Array.from(
				new Set([
					...this.getPositioningAnalyses(),
					...configuredContext.sources.map((source) => source.analysis),
				])
			);
			this.closePositioningConfiguration();
			window.dispatchEvent(
				new CustomEvent('positioning-generate-request', {
					detail: {
						task: configuredContext.task,
						analyses,
						sourceContext: this.getPositioningSourceContext(),
						contextLabel: configuration.title,
						configuredContext,
						autoGenerate: true,
					},
				})
			);
		};
	}

	private renderPositioningGroundingSelection(
		panel: HTMLElement,
		configuration: PositioningConfigurationState
	) {
		const grounding = panel.querySelector<HTMLElement>('.positioning-config-grounding');
		if (!grounding) return;
		grounding.replaceChildren();

		const snapshot = configuration.context.groundingSnapshot;
		const hasBoardGrounding = hasGroundingContextBoardData(snapshot);
		const liveCatalog = configuration.groundingEvidenceCatalog.filter(
			(item) => !item.source.startsWith('social-profile:')
		);

		if (!hasBoardGrounding && !liveCatalog.length) {
			grounding.classList.add('hidden');
			return;
		}

		grounding.classList.remove('hidden');
		const heading = document.createElement('strong');
		heading.innerText = 'Grounding for regeneration';
		const help = document.createElement('p');
		help.innerText = hasBoardGrounding
			? 'Open Grounding context in Analyses to explore framing, board concepts, Idea Box, and poster profiles on the board. Click nodes there to include or exclude them when regenerating.'
			: 'Optional live web/news signals captured with this output. Unselect any you do not want used when regenerating.';
		grounding.append(heading, help);

		if (!liveCatalog.length) return;

		const section = document.createElement('section');
		section.className = 'positioning-config-grounding__section';
		const sectionHeading = document.createElement('strong');
		sectionHeading.innerText = 'Live web / news signals';
		section.append(sectionHeading);

		const selectedKeys = new Set(
			(configuration.context.groundingEvidence || [])
				.filter((item) => !item.source.startsWith('social-profile:'))
				.map(groundingEvidenceKey)
		);
		const items = document.createElement('div');
		items.className = 'positioning-config-grounding__items';
		items.setAttribute('role', 'listbox');
		items.setAttribute('aria-label', 'Live grounding signals');
		items.setAttribute('aria-multiselectable', 'true');

		liveCatalog.forEach((item) => {
			const key = groundingEvidenceKey(item);
			const isSelected = selectedKeys.has(key);
			const button = document.createElement('button');
			button.type = 'button';
			button.className = 'positioning-config-grounding__option';
			button.classList.toggle('is-selected', isSelected);
			button.setAttribute('role', 'option');
			button.setAttribute('aria-selected', isSelected ? 'true' : 'false');
			button.title = item.detail || item.title;

			const badge = document.createElement('span');
			badge.className = 'positioning-config-grounding__source';
			badge.innerText = groundingSourceLabel(item.source);

			const copy = document.createElement('span');
			copy.className = 'positioning-config-grounding__copy';
			const titleEl = document.createElement('strong');
			titleEl.innerText = item.title;
			copy.append(titleEl);
			if (item.detail && item.detail !== item.title) {
				const detail = document.createElement('small');
				detail.innerText = item.detail;
				copy.append(detail);
			}

			const state = document.createElement('span');
			state.className = 'positioning-config-grounding__state';
			state.innerText = isSelected ? 'Selected' : 'Off';

			button.append(badge, copy, state);
			button.onclick = () => {
				const profiles = (configuration.context.groundingEvidence || []).filter((candidate) =>
					candidate.source.startsWith('social-profile:')
				);
				const current = (configuration.context.groundingEvidence || []).filter(
					(candidate) => !candidate.source.startsWith('social-profile:')
				);
				const nextLive = isSelected
					? current.filter((candidate) => groundingEvidenceKey(candidate) !== key)
					: [...current, { ...item }];
				configuration.context.groundingEvidence = [...nextLive, ...profiles];
				this.renderPositioningConfiguration();
			};
			items.append(button);
		});

		const summary = document.createElement('p');
		summary.className = 'positioning-config-grounding__summary';
		summary.innerText = `${selectedKeys.size} of ${liveCatalog.length} live signal${
			liveCatalog.length === 1 ? '' : 's'
		} selected for regeneration.`;
		section.append(items, summary);
		grounding.append(section);
	}

	private renderPositioningAnalysisSelections() {
		const configuration = this.positioningConfiguration;
		if (!configuration) return;
		document
			.querySelectorAll<HTMLLIElement>('#analyses-container .analyses-types li')
			.forEach((li) => {
				li.querySelector('.positioning-analysis-selections')?.remove();
				const analysis = li.dataset.analysis as AnalysisKey;
				if (analysis === GROUNDING_CONTEXT_ANALYSIS_KEY) {
					const selectedCount = selectedGroundingContextLabels(
						configuration.context.groundingSnapshot
					).size;
					li.classList.toggle('positioning-used', selectedCount > 0);
					return;
				}
				const sources = configuration.context.sources.filter(
					(source) => source.analysis === analysis
				);
				li.classList.toggle('positioning-used', sources.length > 0);
				if (!sources.length) return;

				const selections = document.createElement('div');
				selections.className = 'positioning-analysis-selections';
				sources.forEach((source) => {
					const sourceElement = document.createElement('section');
					const sourceTitle = document.createElement('strong');
					sourceTitle.innerText = source.cluster;
					sourceElement.append(sourceTitle);
					const nodes = document.createElement('div');
					nodes.className = 'positioning-config-nodes';
					source.nodes.forEach((nodeLabel) => {
						const remove = document.createElement('button');
						remove.type = 'button';
						remove.title = `Remove ${nodeLabel}`;
						remove.innerText = `${nodeLabel} ×`;
						remove.onclick = (event) => {
							event.stopPropagation();
							if (
								nodeLabel.trim().toLocaleLowerCase() ===
								source.cluster.trim().toLocaleLowerCase()
							) {
								configuration.context.sources = configuration.context.sources.filter(
									(candidate) => candidate !== source
								);
								this.renderPositioningConfiguration();
								this.syncPositioningSelection();
								return;
							}
							source.nodes = source.nodes.filter((label) => label !== nodeLabel);
							if (
								source.nodes.every(
									(label) =>
										label.trim().toLocaleLowerCase() ===
										source.cluster.trim().toLocaleLowerCase()
								)
							) {
								configuration.context.sources = configuration.context.sources.filter(
									(candidate) => candidate !== source
								);
							}
							this.renderPositioningConfiguration();
							this.syncPositioningSelection();
						};
						nodes.append(remove);
					});
					sourceElement.append(nodes);
					selections.append(sourceElement);
				});
				li.append(selections);
			});
	}

	completeNodeSelection(node: NCNode) {
		const configuration = this.positioningConfiguration;
		if (!configuration || !this.selections.positioningNodes || !this.activeAnalysis) {
			super.completeNodeSelection(node);
			return;
		}

		if (this.activeAnalysis === GROUNDING_CONTEXT_ANALYSIS_KEY) {
			this.toggleGroundingContextNode(node);
			return;
		}

		const analysis = this.activeAnalysis;
		const clusterName = node.parent?.label || node.label || 'Selected cluster';
		const existingSource = configuration.context.sources.find(
			(source) => source.analysis === analysis && source.cluster === clusterName
		);
		if (!node.parent) {
			if (existingSource) {
				configuration.context.sources = configuration.context.sources.filter(
					(source) => source !== existingSource
				);
			} else {
				const clusterNodes = this.nodes
					.filter((candidate) => candidate.parent?.uid === node.uid && candidate.label)
					.map((candidate) => candidate.label!);
				configuration.context.sources.push({
					analysis,
					analysisName: ANALYSES_TYPES[analysis]?.name || analysis,
					cluster: clusterName,
					nodes: [clusterName, ...clusterNodes],
				});
			}
		} else if (node.label) {
			if (existingSource?.nodes.includes(node.label)) {
				existingSource.nodes = existingSource.nodes.filter((label) => label !== node.label);
				if (
					existingSource.nodes.every(
						(label) =>
							label.trim().toLocaleLowerCase() === clusterName.trim().toLocaleLowerCase()
					)
				) {
					configuration.context.sources = configuration.context.sources.filter(
						(source) => source !== existingSource
					);
				}
			} else if (existingSource) {
				existingSource.nodes = ensurePositioningClusterCenter({
					...existingSource,
					nodes: [...existingSource.nodes, node.label],
				}).nodes;
			} else {
				configuration.context.sources.push(
					ensurePositioningClusterCenter({
						analysis,
						analysisName: ANALYSES_TYPES[analysis]?.name || analysis,
						cluster: clusterName,
						nodes: [node.label],
					})
				);
			}
		}

		this.renderPositioningConfiguration();
		this.syncPositioningSelection();
	}

	centerOnCentralPoint() {
		const positioningMap = document.querySelector<SVGGElement>('.positioning-map-canvas');
		if (positioningMap && !positioningMap.classList.contains('hidden')) {
			const problemCenter = this.positioningWorkspace?.getProblemCenter();
			if (problemCenter) this.centerTo(problemCenter);
			return;
		}
		super.centerOnCentralPoint();
	}

	async zoomToFit(isExporting = false) {
		const positioningMap = this.baseSvgGroup.querySelector<SVGGElement>(
			'.positioning-map-canvas:not(.hidden)'
		);
		const problemCenter = this.positioningWorkspace?.getProblemCenter();

		if (!isExporting && positioningMap && problemCenter) {
			this.centerTo(problemCenter);
			return;
		}

		await super.zoomToFit(isExporting);
	}

	cleanUp() {
		this.positioningWorkspace?.destroy();
		this.positioningWorkspace = null;
		super.cleanUp();
	}

	userSelectionsToUseInAnalysis(): Array<string> {
		return this.userSelections
			.flatMap((item) =>
				item.words.map((word) => {
					const mapping = item.topicMapping?.find(
						(mappingItem) => mappingItem.key.trim() === word.trim()
					);
					if (mapping) return `${mapping.key} ${mapping.topics.join(' ')}`;
					return '';
				})
			)
			.filter((item) => item.trim() !== '');
	}

	toggleIncludeAiToolResults(): void {
		this.includeAiToolResults = !this.includeAiToolResults;
		document.querySelector<HTMLInputElement>('input#ideate-use-ai-results')!.checked =
			this.includeAiToolResults;
		this.refreshCurrentAnalysis();
		this.setupExplainerMessage();
	}

	setupExplainerMessage(): void {
		const userSelectionsString = this.includeAiToolResults
			? `, and ${this.userSelectionsToUseInAnalysis().length} additional AI-suggested concepts`
			: '';
		document.querySelector<HTMLParagraphElement>('.analyses-description')!.innerHTML = this
			.isCollaborative
			? `Using all ${this.inspireLabels.length} concepts on the Inspire board <span class='inspire-key'>highlighted</span>${userSelectionsString}`
			: `Using ${this.inspireLabels.length} concepts you created in Inspire (manually or with AI tools) <span class='inspire-key'>highlighted</span>${userSelectionsString}`;
	}

	setupAnalysisList() {
		const analysesList = document.querySelector('#analyses-container .analyses-types')!;
		removeChildren(analysesList);

		this.setupExplainerMessage();

		const recommendedAnalysisTypes = this.analyses || ([] as Array<AnalysisKey>);
		const showGroundingContext =
			Boolean(this.positioningConfiguration) &&
			hasGroundingContextBoardData(this.positioningConfiguration?.context.groundingSnapshot);

		const analysisEntries = (
			Object.entries(ANALYSES_TYPES) as Array<[AnalysisKey, ClusterAnalysis]>
		).filter(([analysisKey]) => {
			if (analysisKey === 'colors.cluster') return false;
			if (analysisKey === GROUNDING_CONTEXT_ANALYSIS_KEY) return showGroundingContext;
			return true;
		});
		if (showGroundingContext) {
			analysisEntries.sort(([left], [right]) => {
				if (left === GROUNDING_CONTEXT_ANALYSIS_KEY) return -1;
				if (right === GROUNDING_CONTEXT_ANALYSIS_KEY) return 1;
				return 0;
			});
		}

		analysisEntries.forEach(([analysisKey, analysis]) => {
			const li = document.createElement('li');
			li.dataset.analysis = analysisKey;
			if (
				analysisKey !== GROUNDING_CONTEXT_ANALYSIS_KEY &&
				recommendedAnalysisTypes.includes(analysisKey)
			) {
				li.classList.add('recommended');
				li.title = 'Recommended option';
			}
			if (analysisKey === GROUNDING_CONTEXT_ANALYSIS_KEY) {
				li.classList.add('grounding-context');
				li.title = 'Explore captured grounding on the analysis board';
				const selectedCount = selectedGroundingContextLabels(
					this.positioningConfiguration?.context.groundingSnapshot
				).size;
				if (selectedCount > 0) li.classList.add('positioning-used');
			} else if (
				this.positioningConfiguration?.context.sources.some(
					(source) => source.analysis === analysisKey
				)
			) {
				li.classList.add('positioning-used');
			}
			if (analysis.category) {
				li.innerHTML = `<div>${analysis.category}</div><div>${analysis.name}</div>`;
			} else {
				li.innerText = analysis.name;
			}
			if (this.activeAnalysis === analysisKey) {
				li.classList.add('clicked');
			}
			li.onclick = () => {
				if (this.positioningConfiguration) {
					void this.loadPositioningAnalysis(analysisKey, analysis, li, true);
				} else {
					void this.loadAnalysis(analysisKey, analysis, li, true);
				}
			};
			analysesList.append(li);
		});
		if (this.positioningConfiguration) this.renderPositioningAnalysisSelections();
	}

	save(callback?: () => void) {
		if (globalThis.neuroCreate.saveGraph) {
			globalThis.neuroCreate.saveGraph(
				this.graphMode,
				{
					nodes: this.nodes,
					links: this.links,
					clusters: this.clusters,
					analyses: this.analyses,
					activeAnalysis: this.activeAnalysis,
					positioningInsight: this.positioningInsight,
					positioningLayout: this.positioningLayout,
				},
				callback
			);
		}
	}

	toggleLikeCluster(clusterData: NCCluster): void {
		const nextLiked = !clusterData.liked;
		this.clusters.forEach((c) => {
			if (clusterData.group === c.group) {
				c.liked = nextLiked;
			}
		});
		this.save();

		// Add to hearted list
		ncApi
			.like(
				this.boardId,
				'cluster',
				JSON.stringify({
					group: clusterData.group,
					values: clusterData.values,
				}),
				null,
				!nextLiked
			)
			.then(() => {
				refreshHeartedList();
				tutorialEvents.likedCluster(clusterData);
			});
	}

	/**
	 * Function to use with .join to create a new cluster
	 */
	enterCluster(
		elem: d3.Selection<d3.EnterElement, NCCluster, SVGGElement, null>
	): d3.Selection<SVGGElement, NCCluster, SVGGElement, null> {
		const cluster = elem
			.append('g')
			.attr('data-cluster', (d) => d.group)
			.attr('transform', (d, i) => {
				const fallback = CLUSTER_PROPS[i] || { x: 0, y: 0 };
				const coords = [d.layout?.x ?? fallback.x, d.layout?.y ?? fallback.y];
				return `translate(${coords[0]}, ${coords[1]})`;
			});

		appendClusterCircle(cluster);

		return cluster;
	}

	setClustersAndAnalyses(data: NCGraphData) {
		this.clusters = data.clusters || [];
		const incomingAnalyses = (data.analyses || []).filter(
			(analysis) => analysis !== 'colors.cluster'
		);
		const inherited = resolveIdeatePositioning(globalThis.neuroCreate.doc?.data);
		this.analyses = incomingAnalyses.length
			? incomingAnalyses
			: inherited.analyses.filter((analysis) => analysis !== 'colors.cluster');
		this.activeAnalysis =
			data.activeAnalysis === 'colors.cluster' ? undefined : data.activeAnalysis || undefined;
		this.positioningInsight =
			data.positioningInsight || this.positioningInsight || inherited.positioningInsight;
	}

	/**
	 * Update graph with new data from collaborators / self (via ShareDB)
	 */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	loadNewGraphData(graphData: NCGraphData, preDrawFunction: null | (() => void) = null) {
		super.loadNewGraphData(graphData, () => {
			this.setClustersAndAnalyses(graphData);
			if (graphData.positioningLayout) {
				this.applyPositioningLayout(graphData.positioningLayout);
			}

			if (this.groups.clusters) {
				this.groups.clusters = this.groups.clusters
					.data<NCCluster>(this.clusters, clusterKeyFn)
					.join<SVGGElement>(
						this.enterCluster.bind(this),
						updateCluster,
						this.graphProps.isDeleteAnimated ? removeEl : removeNoDeleteAnimation
					);
			}
		});
	}

	getMenuOptions() {
		return [
			{ label: 'Generate', enabled: true, requiresAi: true, longText: true, shortText: true },
			{ label: 'Search', enabled: true, longText: true, shortText: true },
			{ label: 'Like', enabled: true, longText: true, shortText: true },
		];
	}

	isMovable() {
		return false;
	}

	isPhysicsActive() {
		return false;
	}

	areNodesColoured() {
		return true;
	}

	areLinksColoured() {
		return false;
	}

	getClusterWithLabel(label: string): { cluster: NCCluster; props: ClusterProps } | undefined {
		const index = this.clusters.findIndex((node) => node.group === label);
		if (index >= 0) {
			const cluster = this.clusters[index];
			const fallback = CLUSTER_PROPS[index];
			const props = cluster.layout
				? {
						x: cluster.layout.x,
						y: cluster.layout.y,
						colourClass: fallback.colourClass,
						radius: cluster.layout.orbitRadius,
					}
				: fallback;
			return { cluster, props };
		}
		return undefined;
	}

	get graphMode(): GraphMode {
		return 'ideate';
	}

	startTutorial() {
		super.startTutorial();
		if (!this.shouldShowRecommenderForm()) {
			if (this.templateId) {
				templateTutorial.start(this.templateId, this, true);
			} else {
				startIdeateTutorial();
			}
		}
	}

	startHints() {
		super.startHints();
		if (!this.shouldShowRecommenderForm()) {
			if (isMiniTutorialHidden('ideate') || this.currentUser.settings.shown_ideate) {
				this.setHintsShown();
			} else {
				hints.beginIdeateHint();
			}
		}
	}

	setHintsShown() {
		super.setHintsShown();
		this.currentUser.settings.shown_ideate = true;
		this.saveUser();
	}

	getHintsShown(): boolean {
		return this.currentUser.settings.shown_ideate || false;
	}

	applyPositioningLayout(layout: PositioningLayout) {
		this.positioningLayout = layout;
		this.positioningWorkspace?.applyLayout(layout);
	}

	addToBoard(): Promise<NCNode> {
		throw new Error('Method not available for Ideate.');
	}

	get graphData(): NCGraphData {
		return {
			nodes: this.nodes,
			links: this.links,
			groups: this.nodeGroups,
			clusters: this.clusters,
			analyses: this.analyses,
			activeAnalysis: this.activeAnalysis,
			positioningInsight: this.positioningInsight,
			positioningLayout: this.positioningLayout,
		};
	}

	getGreenNodesForCluster(cluster: NCCluster): Array<NCNode> {
		const clusterNodes = this.nodes.filter((node) => node.parent?.label === cluster.group);
		return clusterNodes.filter((node) => !!node.nodeColour && node.nodeColour === 'IDEATE');
	}
}

export default IdeateGraph;
